import {
  Button,
  Card,
  Center,
  Container,
  Drawer,
  Group,
  Loader,
  Text,
  Stack,
  ScrollArea,
  Divider,
  Tooltip,
} from '@mantine/core';
import { CollectionInvitesButton } from '~/components/Collections/CollectionCollaborators/CollectionInvitesButton';
import { MyCollections } from '~/components/Collections/MyCollections';
import { SUBNAV_STICKY_GAP, useSubnavBottom } from '~/hooks/useSubnavBottom';
import { useDisclosure } from '@mantine/hooks';
import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconPlus,
} from '@tabler/icons-react';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useContainerSmallerThan } from '~/components/ContainerProvider/useContainerSmallerThan';
import { dialogStore } from '~/components/Dialog/dialogStore';
import { useState } from 'react';
import dynamic from 'next/dynamic';
import classes from './CollectionsLayout.module.scss';
import { LegacyActionIcon } from '~/components/LegacyActionIcon/LegacyActionIcon';

const CollectionEditModal = dynamic(() => import('~/components/Collections/CollectionEditModal'));

const MyCollectionsDrawer = () => {
  const [drawerOpen, { close, toggle }] = useDisclosure();

  return (
    <>
      <Button
        className={classes.drawerButton}
        onClick={toggle}
        mb="sm"
        pl={5}
        pr={8}
        variant="default"
      >
        <Group gap={4}>
          <IconLayoutSidebarLeftExpand />
          Collections
        </Group>
      </Button>
      <Drawer
        opened={drawerOpen}
        onClose={close}
        size="100%"
        title={
          <Group gap="xs" wrap="nowrap">
            <Text size="lg" fw={500}>
              Collections
            </Text>
            <CollectionInvitesButton />
          </Group>
        }
        classNames={{ header: classes.drawerHeader, body: 'px-0' }}
      >
        <MyCollections onSelect={() => close()}>
          {({ FilterBox, Filters, ListMenu, Collections, InviteList }) => (
            <Stack gap={4}>
              <Stack gap="xs" px="sm">
                {InviteList}
                <Group gap="xs" wrap="nowrap">
                  <div style={{ flex: 1 }}>{FilterBox}</div>
                  {ListMenu}
                </Group>
                {Filters}
              </Stack>
              <Divider />
              <ScrollArea.Autosize mah="calc(100vh - 105px)">{Collections}</ScrollArea.Autosize>
            </Stack>
          )}
        </MyCollections>
      </Drawer>
    </>
  );
};

const CollectionsLayout = ({ children }: { children: React.ReactNode }) => {
  const isMobile = useContainerSmallerThan('sm');
  const currentUser = useCurrentUser();
  const [showSidebar, setShowSidebar] = useState(true);
  const subnavBottom = useSubnavBottom();

  return (
    <Container fluid className={classes.container}>
      {/* The mobile drawer has always checked this; the desktop card never did,
          so a signed-out visitor got an empty "Collections" panel. */}
      {!!currentUser && (
        <MyCollections>
          {({ FilterBox, Filters, ListMenu, Collections, InviteList, isLoading }) => (
            <Card
              className={classes.sidebar}
              w={300}
              mr="xs"
              p={0}
              style={{
                // No overflow here: the toggle below is positioned in the gutter
                // (right: -32px) and an inline `overflow: hidden` would out-rank the
                // `overflow: visible` on .sidebar and clip it out of existence. The
                // list scrolls in its own ScrollArea, so the Card does not need to.
                marginLeft: showSidebar ? 0 : 'calc(-300px - var(--mantine-spacing-xs))',
                // ⚠️ Does NOT account for the adhesive ad, which is a later in-flow child of the
                // 100%-height `#__next` column and so shortens the scroll area without shortening
                // the viewport — the sidebar's last rows sit behind it for users who see ads.
                // Sizing this off the scroll area instead fixes that but exposes a second problem:
                // the list below carries its own independent `68vh` cap, so a shorter card just
                // makes the list overflow it (the card is `overflow: visible` for the gutter
                // toggle). Both caps have to become one budget before this can shrink safely.
                maxHeight: 'calc(100dvh - var(--header-height) - var(--footer-height) - 68px)',
                // Follows the subnav's real bottom edge rather than a fixed header offset: the
                // subnav hides by translating, so it keeps its layout box and a static `top`
                // strands the sidebar a subnav-height below where it should sit.
                top: subnavBottom + SUBNAV_STICKY_GAP,
              }}
              withBorder
            >
              <Tooltip label="Toggle Sidebar" position="right" openDelay={500}>
                <LegacyActionIcon
                  onClick={() => setShowSidebar((val) => !val)}
                  className={classes.sidebarToggle}
                >
                  {!showSidebar ? (
                    <IconLayoutSidebarLeftExpand />
                  ) : (
                    <IconLayoutSidebarLeftCollapse />
                  )}
                </LegacyActionIcon>
              </Tooltip>
              <Card.Section p="xs" mx={0} className="border-t-0" withBorder>
                <Group justify="space-between" wrap="nowrap">
                  <Text fw={500}>Collections</Text>
                  <Group gap={4} wrap="nowrap">
                    <CollectionInvitesButton />
                    <Button
                      onClick={() => {
                        dialogStore.trigger({
                          component: CollectionEditModal,
                        });
                      }}
                      variant="subtle"
                      size="compact-sm"
                      rightSection={<IconPlus size={14} />}
                    >
                      Create
                    </Button>
                  </Group>
                </Group>
              </Card.Section>

              {InviteList}

              <Card.Section p="xs" mx={0} withBorder>
                <Stack gap="xs">
                  <Group gap="xs" wrap="nowrap">
                    <div style={{ flex: 1 }}>{FilterBox}</div>
                    {ListMenu}
                  </Group>
                  {Filters}
                </Stack>
              </Card.Section>
              {isLoading && (
                <Center py="md">
                  <Loader type="bars" />
                </Center>
              )}
              <Card.Section className="relative h-full" mx={0}>
                <ScrollArea.Autosize mah="calc(68vh - var(--header-height,0))" pb="md">
                  {Collections}
                </ScrollArea.Autosize>
              </Card.Section>
            </Card>
          )}
        </MyCollections>
      )}
      <div className={classes.content}>
        {!!currentUser && isMobile && <MyCollectionsDrawer />}
        {children}
      </div>
    </Container>
  );
};

export { CollectionsLayout };
